import { useReactTable } from "@tanstack/react-table";
import React, { useMemo } from "react";
import type { Transaction } from "../../../Contexts/TransactionsContext";
import { Button, Input, Select } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";
import { useLocalizedStrings } from "../../../Localization";
import { useTransactionsContext } from "../../../Contexts/TransactionsContext";
import { paymentTypeToString } from "../../../utils/payment_utils";

export type TransactionsFilterProps = {
  table: ReturnType<typeof useReactTable<Transaction>>
  setActiveFilters: React.Dispatch<React.SetStateAction<number>>
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  onApplyFilters: (startDate: string, endDate: string) => void
  defaultStartDate: string,
  defaultEndDate: string
}

export type Filters = {
  transactionStart: string,
  transactionEnd: string,
  rcStatus: string,
  transactionState: string,
  entryType: string,
  paymentProvider: string,
}

export const TransactionsFilter: React.FC<TransactionsFilterProps> = ({
  table, 
  setActiveFilters,
  filters,
  setFilters,
  onApplyFilters,
  defaultStartDate,
  defaultEndDate
}) => {
  const L = useLocalizedStrings()
  const { transactions } = useTransactionsContext()

  // Get unique payment providers and create options with beautified names
  const paymentProviderOptions: Array<SelectOptionType> = useMemo(() => {
    const uniqueProviders = new Set(transactions.map(t => t.payment_provider));
    const options: Array<SelectOptionType> = [
      { value: "", label: "All providers" },
    ];
    
    Array.from(uniqueProviders).sort().forEach(provider => {
      options.push({
        value: provider,
        label: paymentTypeToString(provider)
      });
    });
    
    return options;
  }, [transactions, L]);
  
  const applyFilters = () => {
    let activeFilters = 0;

    // Fetch transactions with the date range
    if (filters.transactionStart && filters.transactionEnd) {
      onApplyFilters(filters.transactionStart, filters.transactionEnd)
    }

    const transactionDateCol = table.getColumn("transaction_clear_date")
    if (filters.transactionStart !== "" || filters.transactionEnd !== "") activeFilters++
    transactionDateCol?.setFilterValue(
      filters.transactionStart || filters.transactionEnd
        ? { transactionStart: filters.transactionStart, transactionEnd: filters.transactionEnd }
        : undefined
    )

    const rcStatusCol = table.getColumn("rc_status")
    if (filters.rcStatus !== "") activeFilters++
    rcStatusCol?.setFilterValue(filters.rcStatus || undefined)

    const transactionStateCol = table.getColumn("transaction_state")
    if (filters.transactionState !== "") activeFilters++
    transactionStateCol?.setFilterValue(filters.transactionState || undefined)

    const entryTypeCol = table.getColumn("entry_type")
    if (filters.entryType !== "") activeFilters++
    entryTypeCol?.setFilterValue(filters.entryType || undefined)

    const paymentProviderCol = table.getColumn("payment_provider")
    if (filters.paymentProvider !== "") activeFilters++
    paymentProviderCol?.setFilterValue(filters.paymentProvider || undefined)

    setActiveFilters(activeFilters)
  }

  const resetFilters = () => {
    setFilters({
      transactionStart: defaultStartDate,
      transactionEnd: defaultEndDate,
      rcStatus: "",
      transactionState: "",
      entryType: "",
      paymentProvider: "",
    })
    setActiveFilters(0)
    table.resetColumnFilters()
    // Re-fetch with default dates when resetting
    onApplyFilters(defaultStartDate, defaultEndDate)
  }

  const RC_STATUS_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: L.admin_panel.payments.all_statuses,
    }, 
    {
      value: "PROPOSED",
      label: "PROPOSED",
    },
    {
      value: "ANTICIPATED",
      label: "ANTICIPATED",
    },
    {
      value: "PENDING",
      label: "PENDING",
    },
    {
      value: "SCHEDULED",
      label: "SCHEDULED",
    },
    {
      value: "FAILED",
      label: "FAILED",
    },
    {
      value: "CANCELLED",
      label: "CANCELLED",
    },
    {
      value: "COMPLETE",
      label: "COMPLETE",
    }
  ]

  const TRANSACTION_STATE_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: L.admin_panel.payments.all_statuses,
    },
    {
      value: "pending",
      label: "Pending",
    },
    {
      value: "cleared",
      label: "Cleared",
    },
    {
      value: "failed",
      label: "Failed",
    },
    {
      value: "cancelled",
      label: "Cancelled",
    }
  ]

  const ENTRY_TYPE_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: "All types",
    },
    {
      value: "grant_out",
      label: "Grant Out",
    },
    {
      value: "grant_source",
      label: "Grant Source",
    },
    {
      value: "return_in",
      label: "Return In",
    }
  ]

  const getRcStatusOption = (value: string) => (RC_STATUS_FILTER_VALUES.find((v) => v.value === value))
  const getTransactionStateOption = (value: string) => (TRANSACTION_STATE_FILTER_VALUES.find((v) => v.value === value))
  const getEntryTypeOption = (value: string) => (ENTRY_TYPE_FILTER_VALUES.find((v) => v.value === value))
  const getPaymentProviderOption = (value: string) => (paymentProviderOptions.find((v) => v.value === value))

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.transactions.transaction_date}</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label="Transaction date from"
              type="date"
              value={filters.transactionStart}
              onChange={(e) => setFilters(f => ({ ...f, transactionStart: e.target.value }))}
              className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
            />
            <span className="text-gray-700">to</span>
            <input
              aria-label="Transaction date to"
              type="date"
              value={filters.transactionEnd}
              onChange={(e) => setFilters(f => ({ ...f, transactionEnd: e.target.value }))}
              className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
            />
          </div>
        </div>

        <div>
          <Select 
            className="h-10" 
            options={RC_STATUS_FILTER_VALUES} 
            description={L.admin_panel.transactions.rc_status_desc}
            label={L.admin_panel.transactions.rc_status} 
            labelLocation="above"
            value={getRcStatusOption(filters.rcStatus)} 
            onChange={(value) => setFilters(f => ({... f, rcStatus: value.value}))}
          />
        </div>

        <div>
          <Select 
            className="h-10" 
            options={TRANSACTION_STATE_FILTER_VALUES} 
            description={L.admin_panel.transactions.transaction_state_desc}
            label={L.admin_panel.transactions.transaction_state} 
            labelLocation="above"
            value={getTransactionStateOption(filters.transactionState)} 
            onChange={(value) => setFilters(f => ({... f, transactionState: value.value}))}
          />
        </div>

        <div>
          <Select 
            className="h-10" 
            options={ENTRY_TYPE_FILTER_VALUES} 
            description={L.admin_panel.transactions.entry_type_desc}
            label={L.admin_panel.transactions.entry_type} 
            labelLocation="above"
            value={getEntryTypeOption(filters.entryType)} 
            onChange={(value) => setFilters(f => ({... f, entryType: value.value}))}
          />
        </div>

        <div>
          <Select 
            className="h-10" 
            options={paymentProviderOptions} 
            description={L.admin_panel.transactions.payment_provider_desc}
            label={L.admin_panel.transactions.payment_provider} 
            labelLocation="above"
            value={getPaymentProviderOption(filters.paymentProvider)} 
            onChange={(value) => setFilters(f => ({... f, paymentProvider: value.value}))}
          />
        </div>
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>{L.admin_panel.payments.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.payments.apply_filters}</Button>
        </div>
      </div>  
    </div>
  )
}

